<template>
  <label
    class="ui-radio radiobutton"
    :class="{ disabled: disabledState }"
    :style="bottomSpace"
  >
    <input
      type="radio"
      :value="value"
      :checked="isChecked"
      :disabled="disabledState"
      @click="handleClick"
      @change="onChange"
    />
    <ui-icon
      size="16"
      :icon="`far ${isChecked ? 'fa-dot-circle' : 'fa-circle'}`"
      :color="colorState"
    />
    <span class="line" />
    <slot name="label">
      <ui-label
        :help-tooltip="helpTooltip"
        :text-bold="false"
        :plan-limits="planLimits"
      >
        {{ label }}
      </ui-label>
    </slot>
  </label>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { useColors, usePlanLimits, type PlanLimitsProp } from '@/composables';
import { uiRadioGroupContextKey } from '@/components/ui/ui-radio/context';

const props = withDefaults(
  defineProps<{
    modelValue?: unknown;
    color?: string;
    click?: (value: unknown) => boolean;
    value?: unknown;
    label?: string;
    helpTooltip?: string;
    disabled?: boolean;
    planLimits?: PlanLimitsProp;
    planLimitsEditable?: boolean;
  }>(),
  {
    label: '',
    helpTooltip: '',
    disabled: false,
    planLimitsEditable: false,
  },
);

const emit = defineEmits<{
  change: [value: unknown];
  'update:modelValue': [value: unknown];
}>();

const $colors = useColors();
const radioGroup = inject(uiRadioGroupContextKey, null);

const { showPlanLimitsIcon, handleClickPlanLimits } = usePlanLimits(
  () => props.planLimits,
  () => props.planLimitsEditable,
);

const disabledState = computed(() => props.disabled || radioGroup?.disabled.value || false);

const colorStateChecked = computed(
  () => props.color || radioGroup?.color.value || $colors.green,
);

const colorState = computed(() => {
  if (disabledState.value) return $colors.gray40;
  return isChecked.value ? colorStateChecked.value : $colors.gray40;
});

const currentModelValue = computed(() => {
  if (props.modelValue !== undefined) return props.modelValue;
  return radioGroup?.modelValue.value;
});

const isChecked = computed(() => currentModelValue.value == props.value);

const bottomSpace = computed(() => {
  if (radioGroup?.styleType.value === 'inline') return;
  return { marginBottom: '7px' };
});

const setValue = (value: unknown) => {
  if (props.modelValue !== undefined) {
    emit('update:modelValue', value);
  }
  radioGroup?.setModelValue(value);
};

const isPlanLimitBlocked = () =>
  !props.planLimitsEditable && showPlanLimitsIcon.value;

const onChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  // Match ui-checkbox: revert native check if plan blocks this option
  if (isPlanLimitBlocked()) {
    target.checked = isChecked.value;
    handleClickPlanLimits();
    return;
  }
  setValue(props.value);
  emit('change', props.value);
};

const handleClick = (event: MouseEvent) => {
  if (isPlanLimitBlocked()) {
    event.preventDefault();
    handleClickPlanLimits();
    return;
  }

  if (props.click?.(props.value) === true) {
    event.preventDefault();
  }
};
</script>

<style lang="scss" scoped>
.ui-label {
  color: var(--color-graphite);
}

.radiobutton {
  display: flex;
  flex-direction: row;
  position: relative;
  cursor: pointer;
  user-select: none;
  align-items: center;
  text-transform: capitalize;
  font-size: 13px;
  line-height: normal;

  .v-icon,
  .ui-icon {
    color: var(--color-gray40);
    font-size: 16px;
    position: relative;
    margin-right: 6px;
    z-index: 0;
  }

  &.disabled {
    cursor: default;
    pointer-events: none;

    .ui-label {
      color: var(--color-gray50);
    }
  }

  & input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }
}
</style>
